import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Checkbox } from 'primereact/checkbox';

import PFButton from '../PFPrime/PFButton';
import {
  getTableConfiguration,
  parseTablePinConfigFromApiResponse,
  parseTablePinConfigFromSavePayload,
  saveTableConfiguration,
} from '../PFPrime/services/table.configuration.service';
import './table-freeze.css';

const frozenLeftEqual = (a = [], b = []) =>
  a.length === b.length && a.every((field, i) => field === b[i]);

const POPOVER_MARGIN = 12;
const POPOVER_GAP = 8;

export default function TableFreezePopover({
  freezeButtonRef,
  freezePopoverRef,
  showFreezePopover,
  togglePopover,
  freezeHeaderDraft,
  setFreezeHeaderDraft,
  freezeOptions,
  setDraftColumnChecked,
  confirmFreeze,
  frozenCount,
  tableKey,
  onConfigLoaded,
  getSavePayload,
  buttonLabel = 'Pin',
  title = 'Pin Header',
}) {
  const serverBaselineRef = useRef({ fixHeader: false, frozenLeft: [] });
  const [pinConfigReady, setPinConfigReady] = useState(false);
  /** Desktop anchored portal: { top, left, width } */
  const [portalPlacement, setPortalPlacement] = useState(null);

  const inputIdRootRef = useRef(
    `pf-tf-${Math.random().toString(36).slice(2, 11)}`
  );
  const fieldInputId = suffix => `${inputIdRootRef.current}-${suffix}`;

  const blurPopoverCheckboxFocus = () => {
    requestAnimationFrame(() => {
      const el = document.activeElement;
      if (
        el &&
        typeof el.blur === 'function' &&
        el.closest?.('.pf-table-freeze-popover .p-checkbox')
      ) {
        el.blur();
      }
    });
  };

  const getMaxFreezeColumns = () => {
    const w = window.innerWidth;
    if (w < 768) return 1;
    if (w < 1200) return 4;
    if (w < 1536) return 6;
    return 8;
  };

  const [maxFreezeColumns, setMaxFreezeColumns] = useState(getMaxFreezeColumns);

  useEffect(() => {
    const handleResize = () => setMaxFreezeColumns(getMaxFreezeColumns());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const computeDesktopPortalPlacement = useCallback(() => {
    const btn = freezeButtonRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - POPOVER_MARGIN * 2);
    let left = rect.right - width;
    left = Math.max(
      POPOVER_MARGIN,
      Math.min(left, window.innerWidth - width - POPOVER_MARGIN)
    );
    let top = rect.bottom + POPOVER_GAP;
    return { top, left, width, rect };
  }, []);

  const refineTopForPopoverHeight = useCallback((box, rect, popEl) => {
    if (!popEl) return box;
    const h = popEl.offsetHeight;
    let { top } = box;
    if (top + h > window.innerHeight - POPOVER_MARGIN) {
      const above = rect.top - h - POPOVER_GAP;
      top = above >= POPOVER_MARGIN ? above : POPOVER_MARGIN;
    }
    return { top, left: box.left, width: box.width };
  }, []);

  useLayoutEffect(() => {
    if (!showFreezePopover) {
      setPortalPlacement(null);
      return undefined;
    }

    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches
    ) {
      setPortalPlacement(null);
      return undefined;
    }

    let rafId = 0;

    const sync = () => {
      const next = computeDesktopPortalPlacement();
      if (!next) return;
      const { rect, ...box } = next;
      const pop = freezePopoverRef.current;
      setPortalPlacement(refineTopForPopoverHeight(box, rect, pop));
      rafId = requestAnimationFrame(() => {
        const again = computeDesktopPortalPlacement();
        if (!again) return;
        const { rect: r2, ...box2 } = again;
        setPortalPlacement(
          refineTopForPopoverHeight(box2, r2, freezePopoverRef.current)
        );
      });
    };

    sync();
    window.addEventListener('resize', sync);
    document.addEventListener('scroll', sync, true);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', sync);
      document.removeEventListener('scroll', sync, true);
    };
  }, [
    showFreezePopover,
    computeDesktopPortalPlacement,
    refineTopForPopoverHeight,
    pinConfigReady,
    freezeOptions.length,
    maxFreezeColumns,
  ]);

  const checkedCount = freezeOptions.filter(o => o.checked).length;
  const currentFrozenLeft = freezeOptions
    .filter(o => o.checked)
    .map(o => o.field);
  const baseline = serverBaselineRef.current;
  const isDirtyFromServer =
    Boolean(freezeHeaderDraft) !== Boolean(baseline.fixHeader) ||
    !frozenLeftEqual(currentFrozenLeft, baseline.frozenLeft);
  const hasAnyPinSelection = Boolean(freezeHeaderDraft) || checkedCount > 0;
  const hadSavedPins =
    Boolean(baseline.fixHeader) || baseline.frozenLeft.length > 0;
  const canSavePins =
    pinConfigReady && isDirtyFromServer && (hasAnyPinSelection || hadSavedPins);

  useEffect(() => {
    if (
      !showFreezePopover ||
      !tableKey ||
      typeof onConfigLoaded !== 'function'
    ) {
      setPinConfigReady(false);
      return undefined;
    }
    let cancelled = false;
    setPinConfigReady(false);
    getTableConfiguration(tableKey).then(res => {
      if (cancelled) return;
      const parsed = parseTablePinConfigFromApiResponse(res);
      serverBaselineRef.current = {
        fixHeader: parsed.fixHeader,
        frozenLeft: [...parsed.frozenLeft],
      };
      onConfigLoaded(parsed.fixHeader, parsed.frozenLeft);
      setPinConfigReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [showFreezePopover, tableKey, onConfigLoaded]);

  const handleConfirm = async () => {
    if (!canSavePins) return;
    if (typeof getSavePayload !== 'function' || !tableKey) {
      confirmFreeze();
      return;
    }
    const frozenLeft = currentFrozenLeft;
    const payload = getSavePayload(freezeHeaderDraft, frozenLeft);
    try {
      const res = await saveTableConfiguration(payload);
      if (
        !res ||
        typeof res.status !== 'number' ||
        res.status < 200 ||
        res.status >= 300
      ) {
        throw new Error('Save failed');
      }
      const parsed = parseTablePinConfigFromSavePayload(payload);
      serverBaselineRef.current = {
        fixHeader: parsed.fixHeader,
        frozenLeft: [...parsed.frozenLeft],
      };
      if (typeof onConfigLoaded === 'function') {
        onConfigLoaded(parsed.fixHeader, parsed.frozenLeft);
      }
    } catch (err) {
      console.error('Failed saving table configuration', err);
    }
    confirmFreeze();
  };

  const pinPopoverMarkup = (
    <>
      <div className="pf-table-freeze-popover-close-bar">
        <button
          type="button"
          className="pf-table-freeze-popover-close"
          aria-label="Close"
          onClick={() => togglePopover()}
        >
          <i className="pi pi-times" aria-hidden />
        </button>
      </div>
      <div className="pf-table-freeze-head">
        <div className="pf-table-freeze-title">{title}</div>
        <div className="pf-table-freeze-head-actions">
          <PFButton
            label="Save"
            onClick={handleConfirm}
            disabled={!canSavePins}
          />
        </div>
      </div>

      <div className="pf-table-freeze-section">
        <div className="pf-table-freeze-option pf-table-freeze-option--checkbox-row pf-table-freeze-option--prime-checkbox">
          <Checkbox
            inputId={fieldInputId('header')}
            checked={Boolean(freezeHeaderDraft)}
            onChange={e => {
              setFreezeHeaderDraft(!!e.checked);
              blurPopoverCheckboxFocus();
            }}
          />
          <span className="pf-table-freeze-option-label">Pin Header</span>
        </div>
      </div>

      <div className="pf-table-freeze-section">
        <div className="pf-table-freeze-subtitle">{`Pin Columns (${frozenCount}/${maxFreezeColumns} max)`}</div>
        <div className="pf-table-freeze-columns">
          {freezeOptions.map((option, index) => {
            const limitReached =
              !option.checked && checkedCount >= maxFreezeColumns;
            const isDisabled = option.disabled || limitReached;
            const colId = fieldInputId(`col-${index}`);
            const rowClass =
              `pf-table-freeze-option pf-table-freeze-option--checkbox-row pf-table-freeze-option--prime-checkbox ${
                option.disabled ? 'pf-table-freeze-option-static' : ''
              } ${limitReached ? 'pf-table-freeze-option-limit' : ''}`.trim();
            const rowTitle = limitReached
              ? `Maximum ${maxFreezeColumns} columns can be frozen on this screen size`
              : undefined;

            if (isDisabled) {
              return (
                <div
                  key={`${index}-${option.field}`}
                  className={rowClass}
                  title={rowTitle}
                >
                  <Checkbox
                    inputId={colId}
                    checked={Boolean(option.checked)}
                    disabled
                    onChange={() => {}}
                  />
                  <span className="pf-table-freeze-option-label">
                    {option.header}
                  </span>
                </div>
              );
            }
            return (
              <div
                key={`${index}-${option.field}`}
                className={rowClass}
                title={rowTitle}
              >
                <Checkbox
                  inputId={colId}
                  checked={Boolean(option.checked)}
                  onChange={e => {
                    setDraftColumnChecked(option.columnIndex, !!e.checked);
                    blurPopoverCheckboxFocus();
                  }}
                />
                <span className="pf-table-freeze-option-label">
                  {option.header}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  const portalHost = typeof document !== 'undefined' ? document.body : null;

  const useMobilePinLayout =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 768px)').matches;

  let pinPopoverPortal = null;
  if (showFreezePopover && portalHost) {
    if (useMobilePinLayout) {
      pinPopoverPortal = createPortal(
        <div
          className="pf-freeze-datatable-root"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 6000,
            pointerEvents: 'none',
          }}
        >
          <div
            ref={freezePopoverRef}
            className="pf-table-freeze-popover"
            style={{ pointerEvents: 'auto' }}
          >
            {pinPopoverMarkup}
          </div>
        </div>,
        portalHost
      );
    } else if (portalPlacement) {
      pinPopoverPortal = createPortal(
        <div
          className="pf-freeze-datatable-root"
          style={{
            position: 'fixed',
            top: portalPlacement.top,
            left: portalPlacement.left,
            width: portalPlacement.width,
            zIndex: 6000,
            pointerEvents: 'none',
          }}
        >
          <div
            ref={freezePopoverRef}
            className="pf-table-freeze-popover pf-table-freeze-popover--portal"
            style={{ pointerEvents: 'auto' }}
          >
            {pinPopoverMarkup}
          </div>
        </div>,
        portalHost
      );
    }
  }

  return (
    <>
      <div className="pf-table-freeze-wrapper" ref={freezeButtonRef}>
        <PFButton
          icon="pi pi-thumbtack"
          label={buttonLabel}
          outlined
          className="pf-table-freeze-button"
          onClick={togglePopover}
        />
      </div>
      {pinPopoverPortal}
    </>
  );
}
